import { useState } from "react";
import { Search } from "lucide-react";
import { Checkbox } from "@/v2/components/ui/checkbox";
import { Input } from "@/v2/components/ui/input";
import { Skeleton } from "@/v2/components/ui/skeleton";
import { useAllTeams } from "../api";

type TeamColumnProps = {
  selectedTeam: (string | number)[];
  selectAllTeamPlayers: (string | number)[];
  onSelectionChange: (next: { teams: (string | number)[]; all: (string | number)[] }) => void;
};

const MAX_TEAMS = 3;

export function TeamColumn({ selectedTeam, selectAllTeamPlayers, onSelectionChange }: TeamColumnProps) {
  const { data: teams, isLoading } = useAllTeams();
  const [search, setSearch] = useState("");

  function toggle(teamId: string | number, checked: boolean) {
    if (checked) {
      const keptAll = selectAllTeamPlayers.filter((id) => selectedTeam.includes(id));
      const nextAll = Array.from(new Set([...keptAll, teamId]));
      onSelectionChange({ teams: [...selectedTeam, teamId], all: nextAll });
    } else {
      const nextTeams = selectedTeam.filter((id) => id !== teamId);
      const nextAll = selectAllTeamPlayers.filter((id) => id !== teamId);
      onSelectionChange({ teams: nextTeams, all: nextAll });
    }
  }

  const visibleTeams = (teams ?? []).filter((team) => {
    if (!team.team_name?.trim()) return false;
    return search ? team.team_name.toLowerCase().includes(search.toLowerCase()) : true;
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-card flex flex-col min-h-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="font-bold text-slate-900 text-base">2 · Teams</div>
        <span className="text-sm font-bold px-3 py-1 rounded-full bg-action-light text-action">
          {selectedTeam.length} of {MAX_TEAMS} max
        </span>
      </div>
      <div className="p-2">
        <div className="relative mb-2">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            placeholder="Search Teams"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="overflow-y-auto max-h-72">
          {isLoading ? (
            <div className="flex flex-col gap-2 p-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : visibleTeams.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">No data found</div>
          ) : (
            visibleTeams.map((team, i) => {
              const checked = selectedTeam.includes(team.team_id);
              const disabled = selectedTeam.length === MAX_TEAMS && !checked;
              return (
                <label
                  key={team.team_id}
                  className={`flex items-center gap-4 px-3 py-2 rounded-xl cursor-pointer aria-disabled:opacity-50 aria-disabled:cursor-not-allowed ${
                    checked ? "bg-action-light" : "hover:bg-accent"
                  } ${i > 0 ? "border-t border-slate-100" : ""}`}
                  aria-disabled={disabled}
                >
                  <Checkbox
                    aria-label={team.team_name}
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(next) => toggle(team.team_id, next === true)}
                    className="size-5 rounded-md shrink-0"
                  />
                  <span className="text-base font-semibold text-slate-800 truncate" title={team.team_name}>
                    {team.team_name}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
